import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Alert,
  Typography,
  Paper,
  Grid
} from '@mui/material';

const BusinessInfo = () => {
  const [settings, setSettings] = useState({
    businessName: '',
    businessLegalName: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    businessCountry: '',
    businessPhone: '',
    businessEmail: '',
    businessWebsite: '',
    taxId: '',
    registrationNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/settings/business-info');
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setSettings(prev => ({ ...prev, ...data }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch business info:', err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const res = await fetch('/api/settings/business-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        setMessage({ text: 'Business information saved successfully!', type: 'success' });
      } else {
        const error = await res.json();
        setMessage({ text: error.error || 'Failed to save settings', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box>
      <Stack spacing={3}>
        {/* Basic Information */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" mb={2}>
            Basic Information
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Business Name"
              fullWidth
              size="small"
              value={settings.businessName}
              onChange={(e) => handleChange('businessName', e.target.value)}
              placeholder="Your Store Name"
              helperText="Name displayed to customers"
            />
            <TextField
              label="Legal Business Name"
              fullWidth
              size="small"
              value={settings.businessLegalName}
              onChange={(e) => handleChange('businessLegalName', e.target.value)}
              placeholder="Legal Entity Name"
              helperText="Official registered business name"
            />
          </Stack>
        </Paper>

        {/* Address Information */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" mb={2}>
            Address
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Street Address"
              fullWidth
              size="small"
              value={settings.businessAddress}
              onChange={(e) => handleChange('businessAddress', e.target.value)}
              placeholder="123 Main Street"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  fullWidth
                  size="small"
                  value={settings.businessCity}
                  onChange={(e) => handleChange('businessCity', e.target.value)}
                  placeholder="City"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="State/Province"
                  fullWidth
                  size="small"
                  value={settings.businessState}
                  onChange={(e) => handleChange('businessState', e.target.value)}
                  placeholder="ST"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="ZIP/Postal Code"
                  fullWidth
                  size="small"
                  value={settings.businessZip}
                  onChange={(e) => handleChange('businessZip', e.target.value)}
                  placeholder="12345"
                />
              </Grid>
            </Grid>
            <TextField
              label="Country"
              fullWidth
              size="small"
              value={settings.businessCountry}
              onChange={(e) => handleChange('businessCountry', e.target.value)}
              placeholder="Country"
            />
          </Stack>
        </Paper>

        {/* Contact Information */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" mb={2}>
            Contact Information
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Phone Number"
              fullWidth
              size="small"
              value={settings.businessPhone}
              onChange={(e) => handleChange('businessPhone', e.target.value)}
              placeholder="+1 (555) 123-4567"
            />
            <TextField
              label="Email Address"
              fullWidth
              size="small"
              type="email"
              value={settings.businessEmail}
              onChange={(e) => handleChange('businessEmail', e.target.value)}
              placeholder="info@business.com"
            />
            <TextField
              label="Website"
              fullWidth
              size="small"
              value={settings.businessWebsite}
              onChange={(e) => handleChange('businessWebsite', e.target.value)}
              placeholder="https://www.yourbusiness.com"
            />
          </Stack>
        </Paper>

        {/* Legal Information */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" mb={2}>
            Legal & Tax Information
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Tax ID / EIN"
              fullWidth
              size="small"
              value={settings.taxId}
              onChange={(e) => handleChange('taxId', e.target.value)}
              placeholder="XX-XXXXXXX"
              helperText="Federal Tax Identification Number"
            />
            <TextField
              label="Business Registration Number"
              fullWidth
              size="small"
              value={settings.registrationNumber}
              onChange={(e) => handleChange('registrationNumber', e.target.value)}
              placeholder="Registration/License Number"
              helperText="State or local business registration number"
            />
          </Stack>
        </Paper>

        {message.text && (
          <Alert severity={message.type} onClose={() => setMessage({ text: '', type: '' })}>
            {message.text}
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? 'Saving...' : 'Save Business Information'}
        </Button>
      </Stack>
    </Box>
  );
};

export default BusinessInfo;
